import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Store, Tag, ArrowRight, BadgePercent } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

export default function Partners() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [city, setCity] = useState(sp.get("city") || "");
  const [category, setCategory] = useState(sp.get("category") || "");
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [data, setData] = useState(null);
  useSeo({
    title: "Nos commerces partenaires en Eure-et-Loir | PROXIreduc-28",
    description: "Boutiques, artisans et services locaux près de chez vous. Découvrez-les et profitez de leurs avantages : inscription gratuite.",
    canonicalPath: "/partenaires",
    image: "/og-commerces.jpg",
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/cities").then((r) => setCities((r.data || []).filter((c) => c.business_count > 0))).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (city) params.city = city;
    if (category) params.category = category;
    const t = setTimeout(() => {
      api.get("/public/partners", { params }).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
      setSp(params, { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [q, city, category]); // eslint-disable-line

  const items = data?.items || [];
  const withOffers = useMemo(() => items.filter((i) => i.offer_count > 0).length, [items]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="partners-page">
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-[#E05638] text-[11px] font-bold uppercase tracking-widest">
          <Store size={13} /> Annuaire local
        </div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">Nos commerces partenaires</h1>
        <p className="mt-2 text-slate-600 max-w-2xl">
          Découvrez tous les commerces, artisans et professionnels du réseau PROXIreduc-28 en Eure-et-Loir.
          Soutenez les acteurs locaux près de chez vous.
        </p>
      </header>

      {/* Filtres */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un commerce…"
                 className="pl-9" data-testid="partners-search" />
        </div>
        <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="partners-city"><SelectValue placeholder="Toutes les villes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les villes</SelectItem>
            {cities.map((c) => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="partners-category"><SelectValue placeholder="Toutes les catégories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les catégories</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {data && (
        <div className="mb-5 text-sm text-slate-500" data-testid="partners-count">
          <span className="font-bold text-slate-800">{data.total}</span> commerce{data.total > 1 ? "s" : ""} partenaire{data.total > 1 ? "s" : ""}
          {withOffers > 0 && <span> · {withOffers} avec des offres en cours</span>}
        </div>
      )}

      {!data ? (
        <div className="text-slate-500">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="partners-empty">
          <Store size={36} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-600 font-semibold">Aucun commerce ne correspond à votre recherche.</p>
          <p className="text-sm text-slate-400 mt-1">Essayez d'élargir les filtres.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((b) => (
            <Link key={b.id} to={`/commerce/${b.id}`} data-testid={`partner-card-${b.id}`}
                  className="card-hover group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
              <div className="aspect-[16/10] bg-slate-100 relative">
                {b.cover_image || b.logo ? (
                  <img src={b.cover_image || b.logo} alt={b.name}
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-300"><Store size={44} /></div>
                )}
                {b.offer_count > 0 && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E05638] text-white text-[11px] font-bold shadow">
                    <BadgePercent size={12} /> {b.offer_count} offre{b.offer_count > 1 ? "s" : ""}
                  </span>
                )}
                {b.is_new && (
                  <span className="absolute top-2 right-2 inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide shadow">
                    Nouveau
                  </span>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                {b.category?.name && (
                  <div className="text-[10px] uppercase font-bold tracking-widest text-amber-600 flex items-center gap-1">
                    <Tag size={11} /> {b.category.name}
                  </div>
                )}
                <div className="mt-1 font-heading font-bold text-slate-900 line-clamp-1">{b.name}</div>
                {b.city && <div className="mt-0.5 text-xs text-slate-500 flex items-center gap-1"><MapPin size={11} /> {b.city}</div>}
                {b.description && <p className="mt-2 text-sm text-slate-600 line-clamp-2">{b.description}</p>}
                <div className="mt-auto pt-3 inline-flex items-center gap-1 text-[#E05638] font-semibold text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                  Voir le commerce <ArrowRight size={12} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
